import React, { useLayoutEffect, useMemo, useState, useEffect } from 'react';
import { Dialog, Typography, IconButton } from '@material-ui/core';
import { Close } from '@material-ui/icons';
import { useTranslation } from 'react-i18next';
import { PricingOption } from '../../AppPricingAndSubscription/pricing-option';
import styles from './styles.module.scss';
import { ButtonVariant, CoreButton, TertiaryButton } from '@clover/web-lib-core/components';
import {
  getMeteredBillingString,
  getSubscription,
  SubscriptionObj,
} from '../../../../component-helpers/app-details';
import { useChangeSubscriptionMutation } from '../../../../redux/api/appMarket-api-slice';
import { App, AppSubscriptionCountry } from '../../../../models/app';
import {
  AppActionPostRequestResponseHandler,
  AppActions,
  AppSubscriptionSelectRequestHandler,
} from '../types';
import { InstallAppButton } from '../install';
import { AppInstallResponse } from '../../../../models/post-req-responses-payloads';
import LoadingButton from '../../../Common/LoadingButton';
import { NotificationVariant } from '../../../Common/Notifications/types';
import { AppActionDisclaimer } from '../../../Common/AppActionDisclaimer';
import { isPublicApp } from '../../../../utils/session';
import { Link as RouteLink } from 'react-router-dom';
import { redirectToLoginFromAppDetails } from '../../../../utils/urlConstants';
import { useBillingInfo } from '../../../../contexts/providers/billingInfo';


interface ComponentProps {
  app: App;
  appSubscriptionSelectHandler?: AppSubscriptionSelectRequestHandler;
  open: boolean;
  subscriptionObj?: SubscriptionObj;
  closePricingModal: () => void;
}

export const AppPricingAndSubscriptionModal: React.FC<ComponentProps> = (
  {
    app,
    subscriptionObj,
    appSubscriptionSelectHandler,
    closePricingModal,
    open,
  }): Nullable<JSX.Element> => {
  const { t } = useTranslation();
  const [changeSubscription] = useChangeSubscriptionMutation();
  const [processing, setProcessing] = useState<boolean>(false);
  const subscriptionData = useMemo((): SubscriptionObj => {
    return subscriptionObj || getSubscription(app);
  }, [app, subscriptionObj]);
  const { installed: appIsInstalled } = app || {};
  const {
    availableSubscriptionsArr,
    activeMeteredBilling,
    currentlySelectedSubscription,
    bestSubscriptionOption,
    trialText
  } = subscriptionData;
  const prioritySubscription = currentlySelectedSubscription || bestSubscriptionOption || availableSubscriptionsArr[0];
  const [selectedSubscription, setSelectedSubscription] = useState<AppSubscriptionCountry>(prioritySubscription);
  const appSubscription = selectedSubscription?.appSubscription.id ?? '';
  const meteredBillingString = getMeteredBillingString(activeMeteredBilling);
  const actionButton = appIsInstalled ? AppActions.ChangeSubscription : AppActions.Install;
  const actionButtonText = t(actionButton);
  const disableChangeButton = !selectedSubscription || selectedSubscription.id === currentlySelectedSubscription?.id

  useLayoutEffect(() => {
    setProcessing(false);
  }, [app]);

  useLayoutEffect(() => {
    if (!open) setSelectedSubscription(prioritySubscription);
  }, [open, prioritySubscription]);

  useEffect(() => {

    function keyListener(e: KeyboardEvent) {
      if (e.key === "Escape") {
        closePricingModal();
      }
    }
    document.addEventListener("keydown", keyListener);
    return () => document.removeEventListener("keydown", keyListener);
  });

  const handleChangeSubscription = async () => {
    setProcessing(true);
    const { id: appId } = app;
    const reqResponse = await changeSubscription({ appId, appSubscription });
    appSubscriptionSelectHandler?.({
      reqResponse,
      successNotification: {
        variant: NotificationVariant.Green,
        title: 'app_actions.banners.success_subscription_change'
      },
      selectedSubscription
    });
    closePricingModal();
  };

  const handleInstall: AppActionPostRequestResponseHandler<AppInstallResponse> = (args) => {
    const { reqResponse , app } = args;
    closePricingModal();
    appSubscriptionSelectHandler?.({
      reqResponse,
      app
    });
  };

  const handlePricingOptionChange = (subscription: AppSubscriptionCountry) => {
    setSelectedSubscription(subscription);
    document.getElementById('close')?.focus();
  };

  const redirectToAppDetailsPage = redirectToLoginFromAppDetails(app,appSubscription);

  const {billingInfo} = useBillingInfo();
  const billable = billingInfo.appBillable && (app?.billingInfo?.appBillable ?? true);
  return (
    <Dialog onClose={closePricingModal} open={open} maxWidth={false}
            PaperProps={{ classes: { root: styles.pricingDialog } }} disableAutoFocus={false} role="dialog" aria-labelledby={t('app_details.pricing_subscription')}>
      <h3>{t('app_details.pricing_subscription')}</h3>
      <Typography variant='body1' color='textSecondary' gutterBottom data-testid='secondaryText'>
        {billable ? t('app_details.pricing_subscription_info_text') : t('app_details.non_billable_subscription_info_text')}
      </Typography>
      <IconButton
        onClick={closePricingModal}
        className = {styles.closePricingModal}
        aria-label={t('aria.close')}
        data-testid='closeButton'
      >
        <Close />
      </IconButton>
        <div role="radiogroup">
          {
            availableSubscriptionsArr.map((availableSubscription) => {
              return <PricingOption
                key={availableSubscription.id}
                currentlySelectedSubscription={selectedSubscription}
                trialText={trialText}
                handlePricingOptionChange={handlePricingOptionChange}
                isOptionSelected={availableSubscription.id === selectedSubscription?.id}
                meteredBillingString={meteredBillingString}
                subscription={availableSubscription}
              />;
            })
          }
        </div>
      <div className={styles.buttonContainer}>
        {
          actionButton === AppActions.ChangeSubscription ?
            <LoadingButton
              isLoading={processing}
              isDisabled={processing || disableChangeButton}
              onClick={handleChangeSubscription} text={actionButtonText} />
              :
              isPublicApp()?
              <RouteLink to={`/dashboard/login${redirectToAppDetailsPage}`} color="inherit">
                <CoreButton variant={ButtonVariant.PRIMARY}>{t('buttons.login_to_connect')}</CoreButton>
              </RouteLink>
              :
            <InstallAppButton
              actionCallBack={handleInstall}
              app={app}
              appSubscriptionId={appSubscription}
              text={actionButtonText}
            />
        }
        <TertiaryButton onClick={closePricingModal} className={styles.cancel}>
          {t('buttons.cancel')}
        </TertiaryButton>
      </div>
      <AppActionDisclaimer app={app} actionText={actionButtonText}/>
    </Dialog>
  );
};
